import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import {
  useDealEmailBody,
  useResolvedEmailHtml,
} from '@/hooks/use-deal-source-files'
import type { DealEmailBody } from '@/types/api'

export function SourceEmail({ sourceFileId }: { sourceFileId: string }) {
  const body = useDealEmailBody(sourceFileId)

  if (body.isError) {
    return (
      <Typography variant="body-sm" className="leading-5 text-destructive">
        The original email could not be loaded. Files are kept for 180 days, so
        an older message may no longer have one.
      </Typography>
    )
  }

  if (!body.data) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    )
  }

  return <SourceEmailFrame body={body.data} />
}

// The API sanitizes first; the sandbox is the second defence (no scripts, no same-origin).
// allow-popups* is for broker links (API adds rel=noopener) and permits no script.
function SourceEmailFrame({ body }: { body: DealEmailBody }) {
  const { html, unresolvedImages } = useResolvedEmailHtml(body)

  return (
    <div className="flex flex-col gap-3.5">
      <div className="rounded-lg border border-primary/10 bg-primary/[0.02] p-3.5">
        <dl className="grid grid-cols-[62px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs leading-[1.2]">
          <dt className="text-primary/70">From</dt>
          <dd className="break-words">{body.from ?? 'Not recorded'}</dd>
          <dt className="text-primary/70">Subject</dt>
          <dd>{body.subject ?? 'No subject'}</dd>
          {body.date && (
            <>
              <dt className="text-primary/70">Received</dt>
              <dd className="tabular-nums">{formatEmailDate(body.date)}</dd>
            </>
          )}
        </dl>
      </div>

      <div className="rounded-lg border border-primary/10 p-4.5 text-sm leading-[1.65]">
        {html === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : html.trim() === '' ? (
          <Typography variant="body-sm" className="italic text-primary/70">
            This email carried no readable body.
          </Typography>
        ) : (
          <iframe
            title="Original email"
            // No allow-scripts or allow-same-origin: the document cannot run code or reach this page.
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            srcDoc={emailDocument(html)}
            className="h-112 w-full border-0 bg-white"
          />
        )}
      </div>

      <SourceEmailNotes body={body} unresolvedImages={unresolvedImages} />
    </div>
  )
}

// The API strips <style>; ours tames Outlook centring and 600px tables and cannot execute.
const EMAIL_FRAME_STYLE = `<style>
  html, body { margin: 0; padding: 0 }
  body {
    font: 13px/1.65 'Univers', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    color: #0a0a0a;
    overflow-wrap: break-word;
  }
  /* Left, whatever the broker's tool asked for. A message read beside the
     figures extracted from it is reference, not a layout to preserve. */
  body, p, div, td, th, center, [align] { text-align: left !important }
  /* Nothing wider than the frame: a 600px table in a 480px column hid its own
     right-hand edge behind a scrollbar. */
  * { max-width: 100% !important }
  table { width: auto !important; border-collapse: collapse }
  img { height: auto }
  p { margin: 0 0 .6em }
  blockquote {
    margin: 0 0 .6em;
    padding-left: 10px;
    border-left: 2px solid rgba(0, 52, 105, .2)
  }
  hr { border: 0; border-top: 1px solid rgba(0, 52, 105, .15); margin: 12px 0 }
  a { color: #984729 }
</style>`

// Backstop to the sanitizer: nothing remote loads, so a missed tracking pixel can't phone home.
const EMAIL_FRAME_CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'">`

function emailDocument(html: string): string {
  return `${EMAIL_FRAME_CSP}${EMAIL_FRAME_STYLE}${html}`
}

// Composed: toLocaleString joins date and time with a comma.
function formatEmailDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  const date = parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${date} at ${time}`
}

function SourceEmailNotes({
  body,
  unresolvedImages,
}: {
  body: DealEmailBody
  unresolvedImages: number
}) {
  const notes = [
    'Rendered sanitized and sandboxed: scripts, styles and remote images are stripped before this panel sees the message.',
    body.fallbackFromPlainText
      ? 'This email carried no HTML part, so its plain text is shown.'
      : null,
    body.blockedRemoteImages > 0
      ? `${body.blockedRemoteImages} remote ${body.blockedRemoteImages === 1 ? 'image was' : 'images were'} blocked, including any tracking pixels.`
      : null,
    unresolvedImages > 0
      ? `${unresolvedImages} inline ${unresolvedImages === 1 ? 'image' : 'images'} could not be loaded. Files are kept for 180 days.`
      : null,
  ].filter((note): note is string => note !== null)

  return (
    <div className="flex flex-col gap-1">
      {notes.map((note) => (
        <Typography
          key={note}
          variant="body-sm"
          className="italic leading-normal text-primary/70"
        >
          {note}
        </Typography>
      ))}
    </div>
  )
}
